/**
 * SearchableSelect - Single-select dropdown with search capability.
 *
 * A searchable dropdown that renders via portal for proper z-index handling.
 * Supports filtering options, clearing selection, and keyboard navigation.
 *
 * Placement is floating-ui's, not a fixed `trigger.bottom`: a control low on
 * the screen used to list its last options past the viewport edge, out of
 * reach of any scroll (shipcast-45y.5). The list now opens upward when there
 * is more room above, and never grows taller than the space it has.
 *
 * Dismissal: an outside press and Escape are floating-ui's `useDismiss`;
 * Escape is also default-prevented so that, inside a modal `<dialog>`, it
 * closes only this list and not the dialog. Focus leaving for another control
 * (Tab) closes it too — otherwise the list stays pinned over that control
 * (shipcast-qaf3).
 */

import { useState, useRef, useEffect, useMemo, type FocusEvent } from 'react';
import { createPortal } from 'react-dom';
import { useFloating, useDismiss, useInteractions, offset, flip, size, autoUpdate } from '@floating-ui/react';
import { Search, ChevronDown } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';

/** Gap kept between the open list and the viewport edge, in px. */
const VIEWPORT_MARGIN = 8;
/** Below this the list is unusable; better to overflow than to shrink further. */
const MIN_LIST_HEIGHT = 120;

export interface SearchableSelectOption {
  /** Unique value for the option */
  value: string;
  /** Display label */
  label: string;
  /** Whether the option is disabled */
  disabled?: boolean;
}

export interface SearchableSelectProps {
  /** Id set on the trigger button, so a `<label htmlFor>` can target it */
  id?: string;
  /**
   * Id of an element describing this control, wired to the trigger as
   * `aria-describedby`. Needed where the explanatory text is not rendered
   * next to the control — a hover tooltip, say — because a description a
   * sighted user can reach must also reach a screen-reader user.
   */
  describedBy?: string;
  /** Currently selected value (null for no selection) */
  value: string | null;
  /** Callback when selection changes */
  onChange: (value: string | null) => void;
  /** Available options */
  options: SearchableSelectOption[];
  /** Placeholder text when no value selected */
  placeholder?: string;
  /** Placeholder for search input */
  searchPlaceholder?: string;
  /** Message when no options match search */
  emptyMessage?: string;
  /** Message when options array is empty */
  noOptionsMessage?: string;
  /** Allow clearing the selection */
  allowClear?: boolean;
  /** Label for the clear option */
  clearLabel?: string;
  /** What choosing the clear option means — its tooltip, and the empty trigger's */
  clearDescription?: string;
  /** Disable all interactions */
  disabled?: boolean;
  /** Error message (shows error styling) */
  error?: string;
  /** Minimum options before showing search */
  searchThreshold?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SearchableSelect - Searchable single-select dropdown.
 *
 * @example
 * ```tsx
 * <SearchableSelect
 *   value={selectedFeature}
 *   onChange={setSelectedFeature}
 *   options={features.map(f => ({ value: f.id, label: f.name }))}
 *   placeholder="Select feature"
 *   allowClear
 *   clearLabel="No feature (ungrouped)"
 * />
 * ```
 */
export function SearchableSelect({
  id,
  describedBy,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No matches found',
  noOptionsMessage = 'No options available',
  allowClear = false,
  clearLabel = 'None',
  clearDescription,
  disabled = false,
  error,
  searchThreshold = 3,
  className,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Where the dropdown portals to. Inside a modal `<dialog>` it must be the
  // dialog itself: `showModal()` puts the dialog in the browser top layer and
  // makes the rest of the page inert, so a dropdown portaled to document.body
  // would render unclickable underneath it. `position: fixed` keeps viewport
  // coordinates either way (the dialog has no transform ancestor). Resolved
  // in the open-effect, not during render — refs must not be read mid-render.
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Find selected option label
  const selectedOption = useMemo(() => options.find((opt) => opt.value === value), [options, value]);

  // Filter options by search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  // Show search input when we have enough options
  const showSearch = options.length > searchThreshold;

  const close = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  // `autoUpdate` re-places the list on scroll and resize while it is open.
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open, event, reason) => {
      if (open) return;
      // A modal <dialog> treats Escape as a close request — the keydown's
      // default action, which useDismiss's stopPropagation does not cancel.
      // Prevent it, so Escape closes this list and the dialog stays.
      if (reason === 'escape-key') event?.preventDefault();
      close();
    },
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(4),
      flip({ padding: VIEWPORT_MARGIN }),
      size({
        padding: VIEWPORT_MARGIN,
        apply({ availableHeight, rects, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(availableHeight, MIN_LIST_HEIGHT)}px`,
            width: `${Math.max(rects.reference.width, 220)}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([useDismiss(context)]);

  useEffect(() => {
    if (isOpen) setPortalTarget(buttonRef.current?.closest('dialog') ?? document.body);
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, showSearch]);

  // React bubbles focus events through the portal, so this sees focus leaving
  // the trigger and the list alike. Only a move to another element closes the
  // list: with no `relatedTarget` (a click on a non-focusable spot, or Safari,
  // which does not focus a clicked button) the outside press decides instead.
  const handleBlur = (event: FocusEvent) => {
    const next = event.relatedTarget;
    if (!isOpen || !(next instanceof Node)) return;
    if (buttonRef.current?.contains(next) || dropdownRef.current?.contains(next)) return;
    close();
  };

  const handleSelect = (optionValue: string | null) => {
    onChange(optionValue);
    close();
  };

  const inputBaseClass =
    'w-full h-[34px] rounded-sm border px-2 text-sm transition-colors focus:outline-hidden focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-transparent text-text-primary placeholder:text-text-muted dark:text-text-primary';
  const inputErrorClass = 'border-accent-rose focus:border-accent-rose';
  const inputNormalClass =
    'border-border-strong/50 hover:border-border-input-hover dark:border-border-strong/40 dark:hover:border-border-input-hover';

  return (
    <div className={className} onBlur={handleBlur}>
      <button
        ref={(node) => {
          buttonRef.current = node;
          refs.setReference(node);
        }}
        id={id}
        type="button"
        aria-describedby={describedBy}
        disabled={disabled}
        {...getReferenceProps({ onClick: () => !disabled && setIsOpen(!isOpen) })}
        title={selectedOption?.label || (allowClear ? clearDescription : undefined)}
        className={cn(
          inputBaseClass,
          error ? inputErrorClass : inputNormalClass,
          'flex cursor-pointer items-center justify-between gap-1',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <span
          className={cn(
            'truncate',
            selectedOption
              ? `
                text-text-primary
                dark:text-text-primary
              `
              : `text-text-muted`,
          )}
        >
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          className={cn(`size-3.5 shrink-0 text-text-subtle transition-transform`, isOpen && `rotate-180`)}
        />
      </button>

      {error && <p className="mt-0.5 text-xs text-feedback-error">{error}</p>}

      {/* Dropdown - Rendered via Portal (into the nearest modal <dialog>
          ancestor when there is one — see portalTarget above) */}
      {isOpen &&
        portalTarget &&
        createPortal(
          <div
            ref={(node) => {
              dropdownRef.current = node;
              refs.setFloating(node);
            }}
            className="
              flex flex-col overflow-hidden rounded-lg border border-border-strong bg-bg-elevated shadow-xl
              dark:border-border-strong dark:bg-bg-elevated
            "
            style={{ ...floatingStyles, zIndex: 99999 }}
            {...getFloatingProps()}
          >
            {/* Search Input */}
            {showSearch && (
              <div
                className="
                  shrink-0 border-b border-border-strong p-2
                  dark:border-border-strong
                "
              >
                <div className="relative">
                  <Search
                    className="
                      absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-text-subtle
                      dark:text-text-muted
                    "
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="
                      w-full rounded-sm border border-border-strong bg-transparent py-1.5 pr-2 pl-7 text-xs
                      text-text-primary
                      placeholder:text-text-muted
                      focus:ring-1 focus:ring-primary-500 focus:outline-hidden
                      dark:border-border-strong dark:text-text-primary
                    "
                  />
                </div>
              </div>
            )}

            <div className="max-h-64 min-h-0 overflow-y-auto">
              {/* Clear Option */}
              {allowClear && !searchQuery && (
                <button
                  type="button"
                  title={clearDescription}
                  onClick={() => handleSelect(null)}
                  className={cn(
                    `flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors`,
                    value === null
                      ? `
                        bg-bg-muted font-medium text-text-primary
                        dark:bg-bg-subtle dark:text-text-primary
                      `
                      : `
                        text-text-secondary
                        hover:bg-bg-muted
                        dark:text-text-muted
                        dark:hover:bg-bg-subtle
                      `,
                  )}
                >
                  <span className="italic">{clearLabel}</span>
                </button>
              )}

              {/* Options */}
              {options.length === 0 ? (
                <div className="p-3 text-center text-xs text-text-muted">{noOptionsMessage}</div>
              ) : filteredOptions.length === 0 ? (
                <div className="p-3 text-center text-xs text-text-muted">
                  {emptyMessage} "{searchQuery}"
                </div>
              ) : (
                <div
                  className={
                    allowClear && !searchQuery
                      ? `
                        border-t border-border-muted
                        dark:border-border-default
                      `
                      : ''
                  }
                >
                  {filteredOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => !option.disabled && handleSelect(option.value)}
                      disabled={option.disabled}
                      className={cn(
                        'w-full px-3 py-2 text-left text-xs transition-colors',
                        option.disabled && 'cursor-not-allowed opacity-50',
                        value === option.value
                          ? `
                            bg-bg-muted font-medium text-text-primary
                            dark:bg-bg-subtle dark:text-text-primary
                          `
                          : `
                            text-text-secondary
                            hover:bg-bg-muted
                            dark:text-text-muted
                            dark:hover:bg-bg-subtle
                          `,
                      )}
                    >
                      <span className="truncate">{option.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>,
          portalTarget,
        )}
    </div>
  );
}
